import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CircleStop,
  Copy,
  Download,
  FileBarChart,
  Settings2,
  Pause,
  Play,
  PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useDownloadPendingNumbers,
  useGenerateCampaignReport,
} from '@/features/campaigns/api';
import { STATUS_MAP } from '@/features/campaigns/types';
import {
  useInboundCampaignPause,
  useInboundCampaignResume,
  useInboundCampaignStart,
  useInboundCampaignStop,
} from '../api';
import type { InboundCampaignRow } from '../types';

type ActionKind = 'start' | 'stop' | 'pause' | 'resume';

interface PendingAction {
  kind: ActionKind;
  campaignId: number;
  campaignName: string;
}

const ACTION_COPY: Record<
  ActionKind,
  { title: string; confirmLabel: string; tone: 'default' | 'destructive' }
> = {
  start: { title: 'Start this campaign?', confirmLabel: 'Start', tone: 'default' },
  stop: { title: 'Stop this campaign?', confirmLabel: 'Stop', tone: 'destructive' },
  pause: { title: 'Pause this campaign?', confirmLabel: 'Pause', tone: 'default' },
  resume: { title: 'Resume this campaign?', confirmLabel: 'Resume', tone: 'default' },
};

interface Props {
  campaign: InboundCampaignRow;
}

export function InboundRowActions({ campaign }: Props) {
  const [pending, setPending] = useState<PendingAction | null>(null);

  const stop = useInboundCampaignStop();
  const start = useInboundCampaignStart();
  const pause = useInboundCampaignPause();
  const resume = useInboundCampaignResume();
  const generate = useGenerateCampaignReport();
  const pendingDownload = useDownloadPendingNumbers();

  const status = campaign.campaignStatus;
  const READY = STATUS_MAP.Ready as number;
  const RUNNING = STATUS_MAP.Running as number;
  const PAUSED = STATUS_MAP.Paused as number;
  const COMPLETE = STATUS_MAP.Complete as number;

  const handleConfirm = () => {
    if (!pending) return;
    const mut =
      pending.kind === 'start'
        ? start
        : pending.kind === 'stop'
          ? stop
          : pending.kind === 'pause'
            ? pause
            : resume;
    mut.mutate(
      { campaignId: pending.campaignId },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? `${pending.kind} request submitted`);
          setPending(null);
        },
        onError: () => {
          toast.error(`Could not ${pending.kind} the campaign.`);
          setPending(null);
        },
      },
    );
  };

  const isAnyPending =
    stop.isPending || start.isPending || pause.isPending || resume.isPending;

  const generateFullReport = () => {
    generate.mutate(
      { campaignId: campaign.campaignId, reportType: 'full' },
      {
        onSuccess: (res) => toast.success(res?.message ?? 'Report queued'),
        onError: () => toast.error('Could not generate the report.'),
      },
    );
  };

  const downloadPendingNumbers = () => {
    pendingDownload.mutate(
      { campaignId: campaign.campaignId },
      {
        onSuccess: (numbers) => {
          if (!numbers || numbers.length === 0) {
            toast.message('No pending numbers for this campaign.');
            return;
          }
          // Legacy slices each entry to first 10 chars (raw mobile only).
          const csv = numbers.map((s) => String(s).substring(0, 10)).join('\n');
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `pending_data_${campaign.campaignId}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success(`${numbers.length} pending numbers downloaded`);
        },
        onError: () => toast.error('Could not fetch pending numbers.'),
      },
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Row actions"
            disabled={isAnyPending}
          >
            <Settings2 className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="truncate">
            {campaign.campaignName}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {status === READY && (
            <DropdownMenuItem
              onClick={() =>
                setPending({
                  kind: 'start',
                  campaignId: campaign.campaignId,
                  campaignName: campaign.campaignName,
                })
              }
            >
              <PlayCircle className="h-4 w-4" />
              Start
            </DropdownMenuItem>
          )}

          {status === RUNNING && (
            <DropdownMenuItem
              onClick={() =>
                setPending({
                  kind: 'pause',
                  campaignId: campaign.campaignId,
                  campaignName: campaign.campaignName,
                })
              }
            >
              <Pause className="h-4 w-4" />
              Pause
            </DropdownMenuItem>
          )}

          {status === PAUSED && (
            <DropdownMenuItem
              onClick={() =>
                setPending({
                  kind: 'resume',
                  campaignId: campaign.campaignId,
                  campaignName: campaign.campaignName,
                })
              }
            >
              <Play className="h-4 w-4" />
              Resume
            </DropdownMenuItem>
          )}

          {(status === RUNNING || status === PAUSED) && (
            <DropdownMenuItem
              onClick={() =>
                setPending({
                  kind: 'stop',
                  campaignId: campaign.campaignId,
                  campaignName: campaign.campaignName,
                })
              }
              className="text-destructive focus:text-destructive"
            >
              <CircleStop className="h-4 w-4" />
              Stop
            </DropdownMenuItem>
          )}

          {status === COMPLETE && (
            <DropdownMenuItem onClick={generateFullReport} disabled={generate.isPending}>
              <FileBarChart className="h-4 w-4" />
              Generate report
            </DropdownMenuItem>
          )}

          {(status === RUNNING || status === PAUSED || status === COMPLETE) && (
            <DropdownMenuItem
              onClick={downloadPendingNumbers}
              disabled={pendingDownload.isPending}
            >
              <Download className="h-4 w-4" />
              Download pending numbers
            </DropdownMenuItem>
          )}

          <DropdownMenuItem asChild>
            <Link to={`/clone-inbound-campaign/${campaign.campaignId}`}>
              <Copy className="h-4 w-4" />
              Clone
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending ? ACTION_COPY[pending.kind].title : ''}
        description={
          pending && (
            <>
              <span className="font-medium text-foreground">{pending.campaignName}</span>{' '}
              will be {pending.kind === 'stop' ? 'stopped' : `${pending.kind}d`}.
            </>
          )
        }
        confirmLabel={pending ? ACTION_COPY[pending.kind].confirmLabel : 'Confirm'}
        tone={pending ? ACTION_COPY[pending.kind].tone : 'default'}
        loading={isAnyPending}
        onConfirm={handleConfirm}
      />
    </>
  );
}
